import React, { useState, useRef , useEffect } from 'react';
import {
    Box, Paper, TextField, Typography, Button, InputAdornment, Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useResponsive from '../hooks/useResponsive';
import loginImage from '../assets/Loginbg.webp';
// import { GoogleLogin } from '@react-oauth/google';
import { generateOTP, registerUserotp } from '../services/Apis';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const RegisterMobile = () => {
    const { isSmallScreen } = useResponsive();
    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: "",
        email: "",
        phoneNumber: "",
        otp: ["", "", "", ""],
    });

    const [showOtp, setShowOtp] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [resendTimer, setResendTimer] = useState(0);
    const [resendLoading, setResendLoading] = useState(false);

    // Add refs for OTP fields
    const otpRefs = useRef([React.createRef(), React.createRef(), React.createRef(), React.createRef()]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setError("");
        setSuccess("");
    };

    const handleOtpChange = (index, value) => {
        if (/^\d?$/.test(value)) {
            const newOtp = [...form.otp];
            newOtp[index] = value;
            setForm({ ...form, otp: newOtp });

            // Auto-focus to next input if value entered and not last field
            if (value && index < otpRefs.current.length - 1) {
                otpRefs.current[index + 1].current.focus();
            }
            // If backspace on empty, focus previous
            if (!value && index > 0) {
                otpRefs.current[index - 1].current.focus();
            }
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!showOtp) {
            if (!form.name || !form.email || !form.phoneNumber) {
                setError("Please fill all fields.");
                return;
            }
            if (!/^\d{10}$/.test(form.phoneNumber)) {
                setError("Mobile number must be 10 digits.");
                return;
            }

            // Call generateOTP with user details
            try {
                await generateOTP({
                    name: form.name.trim(),
                    email: form.email.trim(),
                    phoneNumber: form.phoneNumber.trim(),
                });
                setShowOtp(true);
                setResendTimer(30); // Start 30s timer after first OTP sent
            } catch (err) {
                const msg = err.response?.data?.message || "Failed to send OTP.";
                toast.error(msg);
                setError(msg);
            }
            return;
        }

        if (form.otp.some(d => d === "")) {
            setError("Please enter the full OTP.");
            return;
        }

        try {
            const finalOtp = form.otp.join('');
            await registerUserotp({
                name: form.name.trim(),
                email: form.email.trim(),
                phoneNumber: form.phoneNumber.trim(),
                otp: finalOtp
            });
            setSuccess("Registration successful! Please login.");
            document.cookie = `userName=${encodeURIComponent(form.name)}; path=/; max-age=2592000`; // <-- use cookie
            setForm({ name: "", email: "", phoneNumber: "", otp: ["", "", "", ""] });
            navigate('/login');
        } catch (err) {
            setError(err.response?.data?.message || "Registration failed.");
        }
    };

    // Resend OTP handler
    const handleResendOtp = async () => {
        setResendLoading(true);
        setError("");
        setSuccess("");
        try {
            await generateOTP({
                name: form.name.trim(),
                email: form.email.trim(),
                phoneNumber: form.phoneNumber.trim(),
            });
            setResendTimer(30);
            toast.success("OTP resent successfully!");
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to resend OTP.";
            toast.error(msg);
            setError(msg);
        } finally {
            setResendLoading(false);
        }
    };

    // Timer effect
    useEffect(() => {
        let timer;
        if (resendTimer > 0) {
            timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
        }
        return () => clearTimeout(timer);
    }, [resendTimer]);

    return (
        <Box sx={{ width: '100%', height: '100vh', backgroundImage: `url(${loginImage})`, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative' }}>
            <Box sx={{
                position: 'absolute', right: isSmallScreen ? '50%' : '5%', top: '50%',
                transform: isSmallScreen ? 'translate(50%, -50%)' : 'translateY(-50%)',
                width: '100%', maxWidth: 360, px: 2
            }}>
                <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
                    <Typography variant="h3" align="center" gutterBottom>Register</Typography>

                    <form onSubmit={handleSubmit}>
                        {!showOtp && (
                            <>
                                <TextField
                                    fullWidth
                                    placeholder="User Name"
                                    name="name"
                                    value={form.name}
                                    autoComplete="username"
                                    onChange={handleChange}
                                    margin="normal"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <i className="fas fa-user" style={{ color: '#888', fontSize: '16px' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    placeholder="Email Address"
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    margin="normal"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <i className="fas fa-envelope" style={{ color: '#888', fontSize: '16px' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <TextField
                                    fullWidth
                                    placeholder="Mobile Number"
                                    name="phoneNumber"
                                    value={form.phoneNumber}
                                    onChange={handleChange}
                                    margin="normal"
                                    inputProps={{
                                        inputMode: 'numeric',
                                        pattern: '[0-9]{10}',
                                        maxLength: 10,
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <i className="fas fa-mobile-alt" style={{ color: '#888', fontSize: '16px' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </>
                        )}

                        {showOtp && (
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" sx={{ color: '#bcbcbc' }} gutterBottom>Enter your OTP</Typography>
                                <Grid container spacing={1} justifyContent="center">
                                    {form.otp.map((digit, index) => (
                                        <Grid item key={index}>
                                            <TextField
                                                value={digit}
                                                inputRef={otpRefs.current[index]}
                                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                                inputProps={{
                                                    maxLength: 1,
                                                    inputMode: 'numeric',
                                                    style: { textAlign: 'center' }
                                                }}
                                                sx={{ width: 60 }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            {/* Resend OTP UI */}
                            {showOtp && (
                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={-1}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: resendTimer > 0 || resendLoading ? '#aaa' : '#CB4422',
                                            cursor: resendTimer > 0 || resendLoading ? 'not-allowed' : 'pointer',
                                            fontWeight: 450,
                                            fontSize: 13,
                                            userSelect: 'none'
                                        }}
                                        onClick={() => {
                                            if (resendTimer === 0 && !resendLoading) handleResendOtp();
                                        }}
                                    >
                                        {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                                    </Typography>
                                </Box>
                            )}
                            </Box>
                        )}

                        {error && (
                            <Typography color="error" sx={{ mt: 2, textAlign: "center" }}>
                                {error}
                            </Typography>
                        )}
                        {success && (
                            <Typography color="success.main" sx={{ mt: 2, textAlign: "center" }}>
                                {success}
                            </Typography>
                        )}

                        <Button type="submit" fullWidth variant="contained"
                            sx={{
                                bgcolor: '#e5391f', mt: 5, '&:hover': { bgcolor: '#c62818' }, textTransform: 'none', fontWeight: 'bold'
                            }}
                        >
                            {showOtp ? "Register" : "Next"}
                        </Button>
                    </form>

                    {/* <Divider sx={{ my: 2 }}>or</Divider>

                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <GoogleLogin onSuccess={handleGoogleSuccess}   onError={handleGoogleError} />
                    </Box> */}
                </Paper>
            </Box>
        <ToastContainer position="top-right" />
        </Box>
    );
};

export default RegisterMobile;
